import React, { useState, useEffect, useCallback } from "react";
import Sidebar from "./Sidebar";
import "./testRecordsScreen.css";
import { useNavigate, useLocation } from "react-router-dom";

const ViewTestRecords = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const classNameFromState = location.state?.className || "";

  const [searchQuery, setSearchQuery] = useState("");
  const [selectedClass, setSelectedClass] = useState(classNameFromState);
  const [selectedSubject, setSelectedSubject] = useState("");
  const [selectedSection, setSelectedSection] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [testRecords, setTestRecords] = useState([]);
  const [filteredRecords, setFilteredRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [subjects, setSubjects] = useState([]);
  const [sections, setSections] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedTest, setSelectedTest] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [detailedRecords, setDetailedRecords] = useState([]);
  const [loadingDetails, setLoadingDetails] = useState(false);

  // Cards per page configuration
  const CARDS_PER_PAGE = 10;
  const [currentPage, setCurrentPage] = useState(1);
  const [paginatedRecords, setPaginatedRecords] = useState([]);
  const [showPagination, setShowPagination] = useState(false);

  const classes = [
    "Play Group",
    "Nursery",
    "Prep",
    "Class 1",
    "Class 2",
    "Class 3",
    "Class 4",
    "Class 5",
    "Class 6",
    "Class 7",
    "Class 8",
    "Class Pre 9",
    "Class 9",
    "Class 10",
  ];

  // Fetch test records from backend
  const fetchTestRecords = useCallback(async () => {
    setLoading(true);
    setError("");

    try {
      const params = new URLSearchParams({
        action: "get-test-records",
        // Removed pagination parameters to fetch all data
      });

      if (searchQuery) params.append("test_id", searchQuery);
      if (searchQuery) params.append("testName", searchQuery);
      if (selectedClass) params.append("class", selectedClass);
      if (selectedSubject) params.append("subject", selectedSubject);
      if (selectedSection) params.append("section", selectedSection);
      if (dateFrom) params.append("date_from", dateFrom);
      if (dateTo) params.append("date_to", dateTo);

      const response = await fetch(
        `https://mhsdev.online/MHS-WEB/api/ManageTestSheets.php?${params}`
      );
      const data = await response.json();

      console.log("test Details Count:", data);

      if (data.success) {
        // Group records by unique combination of test fields
        const groupedRecords = groupRecordsByUniqueTest(data.data);

        // Sort by test_date (newest first)
        groupedRecords.sort((a, b) => {
          const dateA = new Date(a.test_date);
          const dateB = new Date(b.test_date);
          return dateB - dateA; // Newest first
        });

        setTestRecords(groupedRecords);
        setFilteredRecords(groupedRecords);

        // Reset to first page when data changes
        setCurrentPage(1);
      } else {
        setError(data.error || "Failed to fetch test records");
        setTestRecords([]);
        setFilteredRecords([]);
      }
    } catch (err) {
      setError("Network error: " + err.message);
      setTestRecords([]);
      setFilteredRecords([]);
    } finally {
      setLoading(false);
    }
  }, [
    searchQuery,
    selectedClass,
    selectedSubject,
    selectedSection,
    dateFrom,
    dateTo,
  ]);

  // Update paginated records when filteredRecords or currentPage changes
  useEffect(() => {
    if (filteredRecords.length > CARDS_PER_PAGE) {
      setShowPagination(true);

      // Calculate start and end indices for current page
      const startIndex = (currentPage - 1) * CARDS_PER_PAGE;
      const endIndex = startIndex + CARDS_PER_PAGE;

      // Get records for current page
      const currentPageRecords = filteredRecords.slice(startIndex, endIndex);
      setPaginatedRecords(currentPageRecords);

      // Calculate total pages
      const totalPages = Math.ceil(filteredRecords.length / CARDS_PER_PAGE);
      setTotalPages(totalPages);
    } else {
      // If 10 or fewer cards, show all without pagination
      setShowPagination(false);
      setPaginatedRecords(filteredRecords);
      setTotalPages(1);
    }
  }, [filteredRecords, currentPage]);

  // Fetch detailed test data
  const fetchTestDetails = async (testId) => {
    setLoadingDetails(true);
    try {
      const response = await fetch(
        `https://mhsdev.online/MHS-WEB/api/ManageTestSheets.php?action=get-test-by-id&test_id=${testId}`
      );
      const data = await response.json();

      if (data.success) {
        setDetailedRecords(data.data);
        return data.data;
      }
    } catch (err) {
      console.error("Failed to fetch test details:", err);
    } finally {
      setLoadingDetails(false);
    }
    return [];
  };

  // Group records by unique combination of test fields
  const groupRecordsByUniqueTest = (records) => {
    if (!records || records.length === 0) return [];

    const grouped = {};

    records.forEach((record) => {
      // Create a unique key for each test
      const uniqueKey = `${record.test_id}`;

      if (!grouped[uniqueKey]) {
        grouped[uniqueKey] = {
          id: uniqueKey,
          test_id: record.test_id,
          testName: record.testName,
          test_no: record.test_no,
          subject: record.subject,
          class: record.class,
          section: record.section,
          test_date: record.test_date,
          total_marks: parseFloat(record.total_marks) || 0,
          student_count: 1,
          avg_percentage: parseFloat(record.percentage) || 0,
          max_marks: parseFloat(record.obtained_marks) || 0,
          min_marks: parseFloat(record.obtained_marks) || 0,
          total_obtained: parseFloat(record.obtained_marks) || 0,
        };
      } else {
        grouped[uniqueKey].student_count += 1;
        grouped[uniqueKey].avg_percentage += parseFloat(record.percentage) || 0;
        grouped[uniqueKey].total_obtained +=
          parseFloat(record.obtained_marks) || 0;
        grouped[uniqueKey].max_marks = Math.max(
          grouped[uniqueKey].max_marks,
          parseFloat(record.obtained_marks) || 0
        );
        grouped[uniqueKey].min_marks = Math.min(
          grouped[uniqueKey].min_marks,
          parseFloat(record.obtained_marks) || 0
        );
      }
    });

    // Calculate average percentage for each test
    Object.values(grouped).forEach((test) => {
      if (test.student_count > 0) {
        test.avg_percentage = (
          test.avg_percentage / test.student_count
        ).toFixed(2);
        test.avg_marks = (test.total_obtained / test.student_count).toFixed(2);
      } else {
        test.avg_percentage = "0.00";
        test.avg_marks = "0.00";
      }
    });

    return Object.values(grouped);
  };

  // Fetch subjects when class is selected
  const fetchSubjects = async (className) => {
    if (!className) {
      setSubjects([]);
      return;
    }

    try {
      const response = await fetch(
        `https://mhsdev.online/MHS-WEB/api/ManageTestSheets.php?action=get-subjects&className=${encodeURIComponent(
          className
        )}`
      );
      const data = await response.json();

      if (data.success) {
        setSubjects(data.data || []);
      } else {
        setSubjects([]);
      }
    } catch (err) {
      console.error("Failed to fetch subjects:", err);
      setSubjects([]);
    }
  };

  // Fetch sections when class is selected
  const fetchSections = async (className) => {
    if (!className) {
      setSections([]);
      return;
    }

    try {
      const response = await fetch(
        `https://mhsdev.online/MHS-WEB/api/ManageTestSheets.php?action=get-class-sections&className=${encodeURIComponent(
          className
        )}`
      );
      const data = await response.json();

      if (data.success) {
        setSections(data.data || []);
      } else {
        setSections([]);
      }
    } catch (err) {
      console.error("Failed to fetch sections:", err);
      setSections([]);
    }
  };

  useEffect(() => {
    fetchTestRecords();
  }, [fetchTestRecords]);

  useEffect(() => {
    if (selectedClass) {
      fetchSubjects(selectedClass);
      fetchSections(selectedClass);
    } else {
      setSubjects([]);
      setSections([]);
    }
  }, [selectedClass]);

  // Local search filter
  useEffect(() => {
    if (!searchQuery) {
      setFilteredRecords(testRecords);
      setCurrentPage(1); // Reset to first page when search changes
      return;
    }

    const filtered = testRecords.filter(
      (test) =>
        test.testName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        test.test_id?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        test.subject?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        false
    );

    setFilteredRecords(filtered);
    setCurrentPage(1); // Reset to first page when search changes
  }, [searchQuery, testRecords]);

  const handleSearch = (e) => {
    e.preventDefault();
    fetchTestRecords();
  };

  const handleResetFilters = () => {
    setSearchQuery("");
    setSelectedClass(classNameFromState || "");
    setSelectedSubject("");
    setSelectedSection("");
    setDateFrom("");
    setDateTo("");
    setCurrentPage(1);
  };

  const handleViewDetails = async (test) => {
    setSelectedTest(test);
    const details = await fetchTestDetails(test.test_id);
    setDetailedRecords(details);
    setIsModalOpen(true);
  };

  const handleViewTest = (testId) => {
    navigate(`/viewTestRecords/${testId}`);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedTest(null);
    setDetailedRecords([]);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    } catch (error) {
      return "Invalid Date";
    }
  };

  const getPerformanceColor = (percentage) => {
    const perc = parseFloat(percentage) || 0;
    if (perc >= 80) return "#00b09b";
    if (perc >= 60) return "#6a11cb";
    if (perc >= 40) return "#f7971e";
    return "#ff416c";
  };

  const getPerformanceLabel = (percentage) => {
    const perc = parseFloat(percentage) || 0;
    if (perc >= 80) return "Excellent";
    if (perc >= 60) return "Good";
    if (perc >= 40) return "Average";
    return "Needs Improvement";
  };

  const getCardColorClass = (index) => {
    const colors = [
      "pastel-blue",
      "pastel-lavender",
      "pastel-mint",
      "pastel-coral",
      "pastel-peach",
      "pastel-aqua",
      "pastel-lilac",
      "pastel-sunshine",
      "pastel-rose",
      "pastel-sky",
      "pastel-lime",
      "pastel-sand",
    ];
    return colors[index % colors.length];
  };

  // Calculate unique classes count
  const getUniqueClassesCount = () => {
    if (!testRecords || testRecords.length === 0) return 0;
    if (selectedClass) return 1;
    const uniqueClasses = [...new Set(testRecords.map((test) => test.class))];
    return uniqueClasses.length;
  };

  // Handle page change
  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <div className="test-records-screen">
      <div className="test-records-dashboard-wrapper">
        <Sidebar />
        <main className="test-records-main-content">
          {/* Header Section */}
          <div className="test-records-content-header">
            <div className="test-records-header-left">
              <h1 className="test-records-main-title">Test Records</h1>
              <p className="test-records-subtitle">
                View and manage all test records with detailed insights
              </p>
            </div>
            <div className="test-records-header-actions">
              <button
                className="test-records-action-btn test-records-secondary-btn"
                onClick={handleResetFilters}
              >
                <span className="test-records-btn-icon">🔄</span>
                <span className="test-records-btn-text">Reset Filters</span>
              </button>
            </div>
          </div>

          {/* Stats Overview */}
          <div className="test-records-stats-overview">
            <div className="test-records-stat-card test-records-stat-gradient-purple">
              <div className="test-records-stat-icon">📊</div>
              <div className="test-records-stat-content">
                <div className="test-records-stat-value">
                  {filteredRecords.length}
                </div>
                <div className="test-records-stat-label">Total Tests</div>
              </div>
            </div>
            <div className="test-records-stat-card test-records-stat-gradient-blue">
              <div className="test-records-stat-icon">🏫</div>
              <div className="test-records-stat-content">
                <div className="test-records-stat-value">
                  {getUniqueClassesCount()}
                </div>
                <div className="test-records-stat-label">Classes</div>
              </div>
            </div>
            <div className="test-records-stat-card test-records-stat-gradient-green">
              <div className="test-records-stat-icon">📚</div>
              <div className="test-records-stat-content">
                <div className="test-records-stat-value">{subjects.length}</div>
                <div className="test-records-stat-label">Subjects</div>
              </div>
            </div>
            <div className="test-records-stat-card test-records-stat-gradient-orange">
              <div className="test-records-stat-icon">👥</div>
              <div className="test-records-stat-content">
                <div className="test-records-stat-value">{sections.length}</div>
                <div className="test-records-stat-label">Sections</div>
              </div>
            </div>
          </div>

          {/* Search and Filter Section */}
          <div className="test-records-search-filter-section">
            <form onSubmit={handleSearch} className="test-records-filter-form">
              <div className="test-records-search-container">
                <div className="test-records-search-wrapper">
                  <div className="test-records-search-icon-wrapper">
                    <svg
                      className="test-records-search-icon"
                      viewBox="0 0 24 24"
                      width="20"
                      height="20"
                    >
                      <path
                        fill="currentColor"
                        d="M15.5 14h-.79l-.28-.27A6.471 6.471 0 0 0 16 9.5 6.5 6.5 0 1 0 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z"
                      />
                    </svg>
                  </div>
                  <input
                    type="text"
                    className="test-records-search-input"
                    placeholder="Search by Test ID, Name, or Subject..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                  {searchQuery && (
                    <button
                      type="button"
                      className="test-records-clear-search"
                      onClick={() => setSearchQuery("")}
                    >
                      ✕
                    </button>
                  )}
                </div>
              </div>

              <div className="test-records-filter-grid">
                <div className="test-records-filter-group">
                  <label className="test-records-filter-label">Class</label>
                  <select
                    className="test-records-filter-select"
                    value={selectedClass}
                    onChange={(e) => setSelectedClass(e.target.value)}
                  >
                    <option value="">All Classes</option>
                    {classes.map((cls, index) => (
                      <option key={index} value={cls}>
                        {cls}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="test-records-filter-group">
                  <label className="test-records-filter-label">Subject</label>
                  <select
                    className="test-records-filter-select"
                    value={selectedSubject}
                    onChange={(e) => setSelectedSubject(e.target.value)}
                    disabled={!selectedClass}
                  >
                    <option value="">All Subjects</option>
                    {subjects.map((subject, index) => (
                      <option key={index} value={subject.name}>
                        {subject.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="test-records-filter-group">
                  <label className="test-records-filter-label">Section</label>
                  <select
                    className="test-records-filter-select"
                    value={selectedSection}
                    onChange={(e) => setSelectedSection(e.target.value)}
                    disabled={!selectedClass}
                  >
                    <option value="">All Sections</option>
                    {sections.map((section, index) => (
                      <option key={index} value={section.section_hint}>
                        {section.section_hint}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="test-records-filter-group">
                  <label className="test-records-filter-label">Date From</label>
                  <input
                    type="date"
                    className="test-records-filter-date"
                    value={dateFrom}
                    onChange={(e) => setDateFrom(e.target.value)}
                  />
                </div>

                <div className="test-records-filter-group">
                  <label className="test-records-filter-label">Date To</label>
                  <input
                    type="date"
                    className="test-records-filter-date"
                    value={dateTo}
                    onChange={(e) => setDateTo(e.target.value)}
                  />
                </div>

                <div className="test-records-filter-group test-records-filter-actions">
                  <button
                    type="submit"
                    className="test-records-filter-btn test-records-filter-apply"
                  >
                    Apply Filters
                  </button>
                </div>
              </div>
            </form>
          </div>

          {/* Loading State */}
          {loading && (
            <div className="test-records-loading">
              <div className="test-records-loading-spinner"></div>
              <p>Loading test records...</p>
            </div>
          )}

          {/* Error State */}
          {error && !loading && (
            <div className="test-records-error">
              <div className="test-records-error-icon">⚠️</div>
              <p>{error}</p>
              <button
                onClick={fetchTestRecords}
                className="test-records-error-retry"
              >
                Retry
              </button>
            </div>
          )}

          {/* Test Records Grid */}
          {!loading && !error && (
            <>
              <div className="test-records-grid-header">
                <h2 className="test-records-section-title">
                  Test Records ({filteredRecords.length})
                  {showPagination && (
                    <span className="test-records-pagination-indicator">
                      {" "}
                      (Page {currentPage} of {totalPages})
                    </span>
                  )}
                </h2>
                <div className="test-records-view-options">
                  <span className="test-records-view-label">Showing:</span>
                  <span className="test-records-view-count">
                    {showPagination ? (
                      <>
                        {(currentPage - 1) * CARDS_PER_PAGE + 1}-
                        {Math.min(
                          currentPage * CARDS_PER_PAGE,
                          filteredRecords.length
                        )}{" "}
                        of {filteredRecords.length} tests
                      </>
                    ) : (
                      `${filteredRecords.length} tests`
                    )}
                  </span>
                </div>
              </div>

              {filteredRecords.length === 0 ? (
                <div className="test-records-empty">
                  <div className="test-records-empty-icon">📊</div>
                  <h3>No Test Records Found</h3>
                  <p>Try adjusting your filters or create a new test.</p>
                  <button
                    className="test-records-empty-btn"
                    onClick={() => navigate("/add-test")}
                  >
                    Create New Test
                  </button>
                </div>
              ) : (
                <>
                  <div className="test-records-grid">
                    {paginatedRecords.map((test, index) => {
                      const performanceColor = getPerformanceColor(
                        test.avg_percentage
                      );
                      const performanceLabel = getPerformanceLabel(
                        test.avg_percentage
                      );

                      return (
                        <div
                          className={`test-records-card test-records-card-${getCardColorClass(
                            index
                          )}`}
                          key={test.id || test.test_id}
                          data-status={performanceLabel.toLowerCase()}
                        >
                          <div className="test-records-card-header">
                            <div className="test-records-test-info">
                              <h3 className="test-records-test-name">
                                {test.testName ||
                                  `Test #${test.test_no || test.test_id}`}
                              </h3>
                              <div className="test-records-test-meta">
                                <div className="test-records-meta-item">
                                  <span className="test-records-meta-icon">
                                    #
                                  </span>
                                  <span className="test-records-meta-value">
                                    {test.test_id}
                                  </span>
                                </div>
                                <div className="test-records-meta-item">
                                  <span className="test-records-meta-icon">
                                    📅
                                  </span>
                                  <span className="test-records-meta-value">
                                    {formatDate(test.test_date)}
                                  </span>
                                </div>
                              </div>
                            </div>

                            <div
                              className="test-records-performance-badge"
                              style={{ backgroundColor: performanceColor }}
                            >
                              {performanceLabel}
                            </div>
                          </div>

                          <div className="test-records-card-body">
                            <div className="test-records-details-grid">
                              <div className="test-records-detail-item">
                                <span className="test-records-detail-label">
                                  Subject
                                </span>
                                <span className="test-records-detail-value">
                                  {test.subject || "N/A"}
                                </span>
                              </div>
                              <div className="test-records-detail-item">
                                <span className="test-records-detail-label">
                                  Class
                                </span>
                                <span className="test-records-detail-value">
                                  {test.class || "N/A"}
                                </span>
                              </div>
                              <div className="test-records-detail-item">
                                <span className="test-records-detail-label">
                                  Section
                                </span>
                                <span className="test-records-detail-value">
                                  {test.section || "N/A"}
                                </span>
                              </div>
                              <div className="test-records-detail-item">
                                <span className="test-records-detail-label">
                                  Total Marks
                                </span>
                                <span className="test-records-detail-value">
                                  {test.total_marks || 0}
                                </span>
                              </div>
                              <div className="test-records-detail-item">
                                <span className="test-records-detail-label">
                                  Students
                                </span>
                                <span className="test-records-detail-value">
                                  {test.student_count || 0}
                                </span>
                              </div>
                              <div className="test-records-detail-item">
                                <span className="test-records-detail-label">
                                  Avg Score
                                </span>
                                <span className="test-records-detail-value">
                                  {test.avg_percentage || 0}%
                                </span>
                              </div>
                            </div>

                            <div className="test-records-progress-section">
                              <div className="test-records-progress-header">
                                <span className="test-records-progress-label">
                                  Performance Range
                                </span>
                                <span className="test-records-progress-stats">
                                  Max: {test.max_marks || 0} | Min:{" "}
                                  {test.min_marks || 0}
                                </span>
                              </div>
                              <div className="test-records-progress-bar">
                                <div
                                  className="test-records-progress-fill"
                                  style={{
                                    width: `${test.avg_percentage || 0}%`,
                                    background: `linear-gradient(90deg, ${performanceColor}, ${performanceColor}80)`,
                                  }}
                                ></div>
                              </div>
                            </div>
                          </div>

                          <div className="test-records-card-footer">
                            <button
                              className="test-records-footer-btn test-records-footer-details-btn"
                              onClick={() => handleViewDetails(test)}
                            >
                              <span className="test-records-footer-btn-icon">
                                📋
                              </span>
                              <span className="test-records-footer-btn-text">
                                Details
                              </span>
                            </button>
                            <button
                              className="test-records-footer-btn test-records-footer-view-btn"
                              onClick={() => handleViewTest(test.test_id)}
                            >
                              <span className="test-records-footer-btn-icon">
                                👁️
                              </span>
                              <span className="test-records-footer-btn-text">
                                View Test
                              </span>
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* Pagination - Only show if more than 10 cards */}
                  {showPagination && (
                    <div className="test-records-pagination">
                      <button
                        className="test-records-pagination-btn"
                        onClick={() => handlePageChange(currentPage - 1)}
                        disabled={currentPage === 1}
                      >
                        Previous
                      </button>

                      <div className="test-records-pagination-pages">
                        {Array.from(
                          { length: Math.min(5, totalPages) },
                          (_, i) => {
                            let pageNum;
                            if (totalPages <= 5) {
                              pageNum = i + 1;
                            } else if (currentPage <= 3) {
                              pageNum = i + 1;
                            } else if (currentPage >= totalPages - 2) {
                              pageNum = totalPages - 4 + i;
                            } else {
                              pageNum = currentPage - 2 + i;
                            }

                            return (
                              <button
                                key={pageNum}
                                className={`test-records-pagination-page ${
                                  currentPage === pageNum ? "active" : ""
                                }`}
                                onClick={() => handlePageChange(pageNum)}
                              >
                                {pageNum}
                              </button>
                            );
                          }
                        )}
                      </div>

                      <button
                        className="test-records-pagination-btn"
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={currentPage === totalPages}
                      >
                        Next
                      </button>
                    </div>
                  )}
                </>
              )}
            </>
          )}

          {/* Details Modal */}
          {isModalOpen && selectedTest && (
            <div className="test-records-modal-overlay">
              <div className="test-records-modal">
                <div className="test-records-modal-header">
                  <h2 className="test-records-modal-title">
                    {selectedTest.testName ||
                      `Test #${
                        selectedTest.test_no || selectedTest.test_id
                      }`}{" "}
                    Details
                  </h2>
                  <button
                    className="test-records-modal-close"
                    onClick={closeModal}
                  >
                    ✕
                  </button>
                </div>

                <div className="test-records-modal-body">
                  {/* Test Summary */}
                  <div className="test-records-modal-summary">
                    <div className="test-records-summary-grid">
                      <div className="test-records-summary-item">
                        <span className="test-records-summary-label">
                          Test ID
                        </span>
                        <span className="test-records-summary-value">
                          {selectedTest.test_id}
                        </span>
                      </div>
                      <div className="test-records-summary-item">
                        <span className="test-records-summary-label">
                          Subject
                        </span>
                        <span className="test-records-summary-value">
                          {selectedTest.subject || "N/A"}
                        </span>
                      </div>
                      <div className="test-records-summary-item">
                        <span className="test-records-summary-label">
                          Class
                        </span>
                        <span className="test-records-summary-value">
                          {selectedTest.class || "N/A"}
                        </span>
                      </div>
                      <div className="test-records-summary-item">
                        <span className="test-records-summary-label">
                          Section
                        </span>
                        <span className="test-records-summary-value">
                          {selectedTest.section || "N/A"}
                        </span>
                      </div>
                      <div className="test-records-summary-item">
                        <span className="test-records-summary-label">Date</span>
                        <span className="test-records-summary-value">
                          {formatDate(selectedTest.test_date)}
                        </span>
                      </div>
                      <div className="test-records-summary-item">
                        <span className="test-records-summary-label">
                          Total Marks
                        </span>
                        <span className="test-records-summary-value">
                          {selectedTest.total_marks || 0}
                        </span>
                      </div>
                      <div className="test-records-summary-item">
                        <span className="test-records-summary-label">
                          Total Students
                        </span>
                        <span className="test-records-summary-value">
                          {selectedTest.student_count || 0}
                        </span>
                      </div>
                      <div className="test-records-summary-item">
                        <span className="test-records-summary-label">
                          Average Score
                        </span>
                        <span className="test-records-summary-value">
                          {selectedTest.avg_percentage || 0}%
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Students List */}
                  <div className="test-records-modal-students">
                    <h3 className="test-records-students-title">
                      Student Results ({detailedRecords.length})
                    </h3>

                    {loadingDetails ? (
                      <div className="test-records-students-loading">
                        <div className="test-records-students-spinner"></div>
                        <p>Loading student details...</p>
                      </div>
                    ) : detailedRecords.length > 0 ? (
                      <div className="test-records-students-table-container">
                        <table className="test-records-students-table">
                          <thead>
                            <tr>
                              <th>Student ID</th>
                              <th>Student Name</th>
                              <th>Obtained Marks</th>
                              <th>Percentage</th>
                              <th>Grade</th>
                            </tr>
                          </thead>
                          <tbody>
                            {detailedRecords.map((record, index) => {
                              const studentGrade = getPerformanceLabel(
                                record.percentage
                              );
                              const studentColor = getPerformanceColor(
                                record.percentage
                              );

                              return (
                                <tr key={index}>
                                  <td>{record.student_id || "N/A"}</td>
                                  <td>{record.student_name || "N/A"}</td>
                                  <td>
                                    <span className="test-records-marks-display">
                                      {record.obtained_marks || 0}/
                                      {selectedTest.total_marks || 0}
                                    </span>
                                  </td>
                                  <td>
                                    <span className="test-records-percentage-display">
                                      {record.percentage || 0}%
                                    </span>
                                  </td>
                                  <td>
                                    <span
                                      className="test-records-grade-badge"
                                      style={{ backgroundColor: studentColor }}
                                    >
                                      {studentGrade}
                                    </span>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <div className="test-records-students-empty">
                        <p>No student records found for this test.</p>
                      </div>
                    )}
                  </div>
                </div>

                <div className="test-records-modal-footer">
                  <button
                    className="test-records-modal-btn test-records-modal-close-btn"
                    onClick={closeModal}
                  >
                    Close
                  </button>
                  <button
                    className="test-records-modal-btn test-records-modal-export-btn"
                    onClick={() => {
                      alert("Export functionality would be implemented here");
                    }}
                  >
                    Export Results
                  </button>
                </div>
              </div>
            </div>
          )}
        </main>
      </div>
    </div>
  );
};

export default ViewTestRecords;
